import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router";
import {
  Menu,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { useAuth } from "../auth/OAuthContext";
import { subscribeToDocumentRealtime } from "../api/realtime";
import {
  isSoundEnabled,
  playDocumentEventSound,
  setSoundEnabled,
  testNotificationSound,
} from "../utils/notificationSound";
import meatrixMiniLogo from "../assets/meatrix-mini-logo.png";
import meatrixLogo from "../assets/meatrix-logo.png";
import figmaAvatar from "../assets/figma-documents/avatar.png";
import panelLeftIcon from "../assets/figma-documents/panel-left.svg";
import dashboardIcon from "../assets/figma-documents/dashboard.svg";
import documentsIcon from "../assets/figma-documents/documents.svg";
import logoutIcon from "../assets/figma-documents/logout.svg";

import { PageHeader, PageHeaderProps } from "./PageHeader";

export { PageHeader };
export type { PageHeaderProps };

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface OrderFlowShellProps {
  title: string;
  subtitle?: string;
  breadcrumb?: string | BreadcrumbItem[];
  actions?: ReactNode;
  children: ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  icon: string;
  section: "Workspace";
  exact?: boolean;
}

const SIDEBAR_COLLAPSED_KEY = "meatrix-connect-vna.sidebar-collapsed";

function readSidebarCollapsed(): boolean {
  try {
    return sessionStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

export function OrderFlowShell({ title, subtitle, breadcrumb, actions, children }: OrderFlowShellProps) {
  const { roles, email, logout, idToken } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [soundActive, setSoundActive] = useState(isSoundEnabled);

  useEffect(() => {
    const handleToggle = (e: Event) => {
      const custom = e as CustomEvent<{ enabled: boolean }>;
      if (custom.detail) {
        setSoundActive(custom.detail.enabled);
      }
    };
    window.addEventListener("orderflow-sound-toggle", handleToggle);
    return () => window.removeEventListener("orderflow-sound-toggle", handleToggle);
  }, []);

  // Global realtime sound listener so any document event produces audio regardless of current view
  useEffect(() => {
    if (!idToken) return;
    const unsubscribe = subscribeToDocumentRealtime(idToken, (event) => {
      if (event.type === "document.changed") {
        playDocumentEventSound(event.action, event.status, event.documentId);
      }
    });
    return unsubscribe;
  }, [idToken]);

  function toggleSidebar() {
    const nextCollapsed = !collapsed;
    setCollapsed(nextCollapsed);
    try {
      sessionStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(nextCollapsed));
    } catch {
      // The navigation still works if browser storage is unavailable.
    }
  }

  function handleToggleSound() {
    const nextState = !soundActive;
    setSoundEnabled(nextState);
    setSoundActive(nextState);
    if (nextState) {
      testNotificationSound();
    }
  }

  const navItems = useMemo(() => buildNavItems(roles), [roles]);
  const roleLabel = roles.includes("Doctor")
    ? "Doctor"
    : roles.includes("Nurse")
    ? "Nurse"
    : roles.includes("Admin")
    ? "Admin"
    : "User";

  const breadcrumbItems = useMemo(
    () => resolveBreadcrumbItems(breadcrumb, location.pathname, title),
    [breadcrumb, location.pathname, title],
  );

  return (
    <div className="min-h-screen flex bg-[#FDFFFC] text-slate-700 antialiased relative font-sans">
      {/* Mobile backdrop scrim */}
      {mobileOpen && (
        <button
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-40 lg:hidden border-0 cursor-pointer"
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Side Rail Navigation (Figma 300px width) */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-[#FDFFFC] border-r border-[#E6E8EA] transition-all duration-300 ease-in-out ${
          collapsed ? "lg:w-[80px]" : "lg:w-[300px]"
        } ${
          mobileOpen ? "translate-x-0 w-[300px]" : "-translate-x-full lg:translate-x-0"
        }`}
        aria-label="Application navigation"
      >
        <div className="h-20 shrink-0 flex items-center justify-center px-4 border-b border-[#E6E8EA]">
          <div className="flex items-center overflow-hidden">
            {collapsed ? (
              <img src={meatrixMiniLogo} alt="MEATrix OrderFlow" className="h-8 w-8 object-contain" />
            ) : (
              <img src={meatrixLogo} alt="MEATrix OrderFlow" className="h-[49px] w-[152px] object-contain" />
            )}
          </div>
          <button
            className="lg:hidden absolute right-3 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            type="button"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <nav className="flex-1 py-4 px-4 flex flex-col gap-6 overflow-y-auto">
          <section>
            {!collapsed && (
              <p className="text-xs font-normal text-[#7F8A92] mb-3">
                Workspace
              </p>
            )}
            <div className="flex flex-col gap-1">
              {navItems.map((item) => {
                const reviewIsDashboard = location.pathname.startsWith("/documents/") && item.to === "/dashboard";
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.exact}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 h-11 rounded-lg text-sm transition-all group ${
                        isActive || reviewIsDashboard
                          ? "bg-[#012699] text-white font-bold shadow-xs"
                          : "text-[#24313D] font-semibold hover:text-[#011627] hover:bg-[#F2F4FA]"
                      } ${collapsed ? "justify-center px-0" : ""} ${reviewIsDashboard ? "review-current" : ""}`
                    }
                    title={item.label}
                  >
                    <img src={item.icon} alt="" className="sidebar-menu-icon shrink-0" aria-hidden="true" />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          </section>
        </nav>
      </aside>

      {/* Main Container Area */}
      <div
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${
          collapsed ? "lg:pl-[80px]" : "lg:pl-[300px]"
        }`}
      >
        {/* Top Header Bar (Figma 80px / h-20) */}
        <header className="h-20 shrink-0 bg-[#FDFFFC] border-b border-[#E6E8EA] sticky top-0 z-30 flex items-center justify-between px-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl text-slate-600 hover:bg-[#F2F4FA] transition-colors"
              type="button"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={22} strokeWidth={1.8} aria-hidden="true" />
            </button>
            <button
              className="hidden lg:flex items-center justify-center w-10 h-10 rounded-xl text-[#7F8A92] hover:text-[#011627] hover:bg-[#F2F4FA] transition-colors cursor-pointer"
              type="button"
              aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
              onClick={toggleSidebar}
            >
              <img src={panelLeftIcon} alt="" aria-hidden="true" />
            </button>
            {/* Breadcrumb Navigation */}
            <nav aria-label="Breadcrumb" className="text-sm font-normal text-[#7F8A92]">
              <ol className="flex items-center gap-1 flex-wrap m-0 p-0 list-none">
                <li className="flex items-center">
                  <span className="text-[#A0AAB1] select-none mr-1" aria-hidden="true">
                    /
                  </span>
                </li>
                {breadcrumbItems.map((item, idx) => {
                  const isLast = idx === breadcrumbItems.length - 1;
                  return (
                    <li key={`${item.label}-${idx}`} className="flex items-center">
                      {item.to ? (
                        <Link
                          to={item.to}
                          className={`transition-colors cursor-pointer ${
                            isLast
                              ? "text-[#011627] font-medium hover:underline"
                              : "text-[#7F8A92] hover:text-[#011627] hover:underline"
                          }`}
                          aria-current={isLast ? "page" : undefined}
                        >
                          {item.label}
                        </Link>
                      ) : (
                        <span
                          aria-current={isLast ? "page" : undefined}
                          className={isLast ? "text-[#011627] font-medium select-none" : "text-[#7F8A92]"}
                        >
                          {item.label}
                        </span>
                      )}
                      {!isLast && (
                        <span className="text-[#A0AAB1] select-none mx-1.5" aria-hidden="true">
                          /
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {/* User Profile Pill (Figma 35:260 - padding: 8px 16px, borderRadius: 32px, border: #E6E8EA) */}
            <div className="flex items-center gap-3 py-2 px-4 rounded-full bg-[#FDFFFC] border border-[#E6E8EA]">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-200 border-2 border-white shrink-0">
                <img src={figmaAvatar} alt={email ?? "User"} className="w-full h-full object-cover" />
              </div>
              <div className="hidden sm:flex flex-col text-left">
                <span className="text-sm font-normal text-[#000000] leading-tight truncate max-w-[160px]">
                  {email ?? "user@email.com"}
                </span>
                <span className="text-xs font-normal text-[#7F8A92]">
                  {roleLabel}
                </span>
              </div>
            </div>

            {/* Sound Notification Toggle Button */}
            <button
              className={`flex items-center justify-center w-11 h-11 rounded-full border transition-colors cursor-pointer ${
                soundActive
                  ? "bg-[#F0FDF4] border-[#86EFAC] text-[#16A34A] hover:bg-[#DCFCE7]"
                  : "bg-slate-100 border-slate-300 text-slate-400 hover:bg-slate-200"
              }`}
              onClick={handleToggleSound}
              type="button"
              title={soundActive ? "Sound notifications enabled (click to mute)" : "Sound notifications muted (click to unmute)"}
              aria-label={soundActive ? "Mute event sounds" : "Unmute event sounds"}
            >
              {soundActive ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>

            {/* Logout Red Pill Button (Figma 35:265 - bg #F9EEEE, border #D83933, text #D83933, borderRadius: 32px) */}
            <button
              className="flex items-center justify-center w-11 h-11 rounded-full bg-[#F9EEEE] border border-[#D83933] text-[#D83933] hover:bg-[#F9EEEE]/80 transition-colors cursor-pointer"
              onClick={() => void logout()}
              type="button"
              title="Sign out"
              aria-label="Sign out"
            >
              <img src={logoutIcon} alt="" aria-hidden="true" />
            </button>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 min-w-0 p-4 w-full">
          <PageHeader title={title} subtitle={subtitle} actions={actions} />
          {children}
        </main>
      </div>
    </div>
  );
}

function buildNavItems(roles: string[]): NavItem[] {
  const items: NavItem[] = [];
  if (roles.includes("Nurse")) {
    items.push({ to: "/dashboard", label: "Dashboard", icon: dashboardIcon, section: "Workspace", exact: true });
    items.push({ to: "/documents", label: "Documents", icon: documentsIcon, section: "Workspace", exact: true });
  } else if (roles.includes("Doctor")) {
    items.push({ to: "/dashboard", label: "Dashboard", icon: dashboardIcon, section: "Workspace", exact: true });
    items.push({ to: "/documents", label: "Documents", icon: documentsIcon, section: "Workspace", exact: true });
  } else {
    items.push({ to: "/dashboard", label: "Dashboard", icon: dashboardIcon, section: "Workspace", exact: true });
  }

  if (roles.includes("Admin")) {
    items.push({ to: "/admin", label: "Admin console", icon: documentsIcon, section: "Workspace", exact: true });
  }

  return items;
}

function inferRouteFromLabel(
  label: string,
  isLast?: boolean,
  currentPathname?: string,
): string | undefined {
  const norm = label.trim().toLowerCase();
  if (norm === "dashboard") return "/dashboard";
  if (norm === "documents") return "/documents";
  if (norm === "user management" || norm === "admin" || norm === "admin console") return "/admin";
  if (norm === "review document") {
    return isLast ? undefined : (currentPathname || "/dashboard");
  }
  if (norm.includes("dashboard")) return "/dashboard";
  if (norm.includes("document")) return "/documents";
  if (norm.includes("user") || norm.includes("admin")) return "/admin";
  return undefined;
}

function resolveBreadcrumbItems(
  breadcrumbProp: string | BreadcrumbItem[] | undefined,
  pathname: string,
  title: string,
): BreadcrumbItem[] {
  if (Array.isArray(breadcrumbProp)) {
    return breadcrumbProp.map((item, idx, arr) => {
      const isLast = idx === arr.length - 1;
      return {
        label: item.label,
        to: item.to !== undefined ? item.to : inferRouteFromLabel(item.label, isLast, pathname),
      };
    });
  }

  if (typeof breadcrumbProp === "string") {
    const rawSegments = breadcrumbProp
      .split("/")
      .map((s) => s.trim())
      .filter(Boolean);

    if (rawSegments.length > 0) {
      return rawSegments.map((segment, idx) => {
        const isLast = idx === rawSegments.length - 1;
        return {
          label: segment,
          to: inferRouteFromLabel(segment, isLast, pathname),
        };
      });
    }
  }

  if (pathname.startsWith("/documents/")) {
    return [
      { label: "Dashboard", to: "/dashboard" },
      { label: "Review document" },
    ];
  }
  if (pathname === "/documents") {
    return [
      { label: "Dashboard", to: "/dashboard" },
      { label: "Documents", to: "/documents" },
    ];
  }
  if (pathname === "/admin") {
    return [
      { label: "Dashboard", to: "/dashboard" },
      { label: "User management", to: "/admin" },
    ];
  }
  return [{ label: title || "Dashboard", to: "/dashboard" }];
}
